// @flow
import React, {useCallback, useEffect, useState} from 'react';
import {Card, CardBody, Col, Row,} from 'reactstrap';
import {group} from '../redux';
import {connect} from 'react-redux';
import AddGroupComponent from './components/AddGroup.js';
import AddGroupUserComponent from './components/AddGroupUser.js';
import TableProvider from '../../ui/components/Table/TableProvider';
import PageTitle from '../../ui/components/PageTitle';
import {useTranslation} from 'react-i18next';
import {hasPermission} from '../../../shared/authUtils';
import {openConfirmation} from "../../ui/redux/actions";
import {selectFilter, textFilter} from 'react-bootstrap-table2-filter';

import {actions as groupActions} from "../redux/group.reducers";
import {any, includes} from 'ramda';
import {getPermissions} from '../../../shared/configUtils';
import ScrollBar from 'react-perfect-scrollbar';


const Status = {
    Active: 'Active',
    Inactive: 'Inactive'
};

const StatusColumn = (cell, row, rowIndex, extraData) => {
    return cell ? Status.Active : Status.Inactive;
};

const DeleteMessage = ({items, data}) => {
    const deletingItems = data.filter(d => includes(d['resource_id'], items))
    const hasAssociatedUsers = deletingItems ? any(item => item['total_users'] > 0, deletingItems) : false;
    const isSingleDelete = deletingItems.length === 1;
    const permissions = getPermissions()

    const groupPermissions = deletingItems.map((item, index) => {
        const permissionNames = item.permission.map((permission, index) => {
            return <span key={index} className='font-weight-normal badge badge-danger mr-1'>
                {permissions.find(p => p.key === parseInt(permission)).value}</span>
        });

        return (
            <div className='font-weight-bold' key={item.resource_id || index}>
                <h5>{item.name}:</h5>
                {permissionNames}
            </div>
        )
    });

    return (
        <ScrollBar>
            <div>
                You are about to delete {items.length} Group(s) from the system.
                {hasAssociatedUsers && (
                    <div>
                        {isSingleDelete && (
                            <span>This will remove the following permissions from <strong>{deletingItems[0]['total_users']} associated users</strong>: <br/></span>
                        )}
                        {!isSingleDelete && (
                            <span>This will remove the following permissions from the associated users: <br/></span>
                        )}
                        {groupPermissions}
                    </div>
                )}
                <br/>
                Please make sure you are aware of the impact.
            </div>
        </ScrollBar>
    )
}

const GroupManagement = props => {
    const initialState = {
        showAddModal: false,
        showAddUserModal: false,
        selectedId: null,
        entityData: null,
        editGroup: false,
        selectedRows: [],
        // onDeleteMessage: 'You are about to delete {0} Group(s) from the system. This action cannot be undone. Please make sure you are aware of the impact.',
        onDeleteMessage: DeleteMessage,
    };

    const [groupState, setGroupState] = useState(initialState);

    useEffect(_ => {
        props.list(props.page, props.sizePerPage, props.searchText, props.sort, props.filters);
    }, []);

    useEffect(_ => {
        const data = props.entity.data;
        if (data && data.data) {
            const entityData = data.data.result;
            setGroupState({...groupState, entityData, showAddModal: groupState.editGroup});
        }
    }, [props.entity, groupState.editGroup]);

    const handleRowSelected = useCallback((selectedId, selectedRows) => {
        setGroupState({...groupState, selectedId, selectedRows});
    });

    const handleLoadData = useCallback((page, sizePerPage, searchText, sort, filters) => {
        props.list(page, sizePerPage, searchText, sort, filters);
    });

    // update & edit action
    const addGroup = _ => {
        setGroupState({
            ...groupState,
            entityData: null,
            showAddModal: true,
            selectedId: null,
        });
    };

    const handleDownload = selected => {
        props.downloadGroups(selected);
    };

    const edit = _ => {
        props.showResource({
            id: groupState.selectedId,
        });
        setGroupState({...groupState, editGroup: true});
    };

    const manageUsers = _ => {
        props.showResource({
            id: groupState.selectedId
        });
        setGroupState({
            ...groupState,
            showAddUserModal: true,
            editGroup: false,
        })
    };

    // close modals
    const closeAddModal = _ => {
        setGroupState({
            ...groupState,
            showAddModal: false,
            showAddUserModal: false,
            entityData: null,
            edit: false,
        });
    };

    const {t} = useTranslation();

    const columns = [
        {
            dataField: 'name',
            text: t('groups.columns.name'),
            sort: true,
            filter: textFilter({delay: 2000})
        },
        {
            dataField: 'description',
            text: t('groups.columns.description'),
            sort: true,
            filter: textFilter({delay: 2000})
        },
        // {
        //     dataField: 'is_active',
        //     text: t('groups.columns.status'),
        //     sort: true,
        //     formatter: StatusColumn,
        //     headerStyle: () => {
        //         return {
        //             width: "10%",
        //         };
        //     },
        //     filter: selectFilter({
        //         options: {
        //             Active: Status.Active,
        //             Inactive: Status.Inactive
        //         }
        //     })
        // },
        {
            dataField: 'total_users',
            text: t('groups.columns.total_users'),
            sort: true,
            headerStyle: () => {
                return {
                    width: "10%",
                };
            },
            filter: textFilter({delay: 2000})
        },
        {
            dataField: 'created_at',
            text: t('groups.columns.created_at'),
            sort: true,
            headerStyle: () => {
                return {width: "10%"};
            }
        },
        {
            dataField: 'last_updated',
            text: t('groups.columns.last_updated'),
            sort: true,
            headerStyle: () => {
                return {width: "10%"};
            }
        },
    ];

    const buttons = [{
        title: t('groups.buttons.create_group'),
        onClick: addGroup,
        color: 'primary',
        classNames: 'mdi mdi-plus-circle',
        hidden: !hasPermission([{name: ['User.FullControl']}]),
    }];

    const selectedRowActions = [
        {
            tooltip: t('groups.buttons.add_users'),
            classNames: 'mdi mdi-account-multiple-plus',
            onClick: manageUsers,
            hidden: !hasPermission([{name: ['User.FullControl']}]) || !groupState.selectedRows || groupState.selectedRows.length !== 1,
        },
    ];

    return (
        <React.Fragment>
            <PageTitle title='Manage Groups'/>
            <Row>
                <Col>
                    <Card>
                        <CardBody>
                            <TableProvider state={props} loading={props.loading} data={props.entities} columns={columns}
                                           keyField='id'
                                           totalSize={props.total} buttons={buttons}
                                           onLoadData={handleLoadData}
                                           onEditData={edit}
                                //    selectedRowActions={selectedRowActions}
                                           onRowSelected={handleRowSelected}
                                           onDownload={handleDownload}
                                           onDeleteData={props.deleteResources}
                                           onDeleteMessage={groupState.onDeleteMessage}
                                           hasModifyPermission={hasPermission([{name: ['User.FullControl']}])}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <AddGroupComponent show={groupState.showAddModal}
                               data={groupState.entityData}
                               selectedId={groupState.selectedId}
                               close={closeAddModal}
            />

            <AddGroupUserComponent show={groupState.showAddUserModal}
                                   data={groupState.entityData}
                                   close={closeAddModal}
            />

        </React.Fragment>
    );
};

const mapStateToProps = state => {
    const {entities, total, page, sizePerPage, searchText, sort, filters, loading, error, entity} = state.Groups;
    return {entities, total, page, sizePerPage, searchText, sort, filters, loading, error, entity};
};

export default connect(
    mapStateToProps,
    {
        list: group.actions.list,
        deleteResources: group.actions.deleteResources,
        showResource: group.actions.show,
        downloadGroups: groupActions.downloadGroups,
        openConfirmation,
    }
)(GroupManagement);
